import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalFooter, ModalHeader, ModalBody } from 'reactstrap';
import PageBreadcrumb from '../../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import { confirmDelete } from '../../../utils/confirm';
import notify from '../../../utils/notify';
import '../../TenderOpening/components/TenderOpening.css';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  FormGrid,
  RequiredNote,
  TextAreaField,
  TextField,
  focusFirstError,
  hasErrors,
  onlyWhen,
  pattern,
  required,
  validateForm as runValidation,
} from '../../../Components/Form';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import ListPager from '../../../Components/Common/ListPager';
import {
  EmptyState,
  SkeletonRows,
} from '../../../Components/Common/ListStates';

const SCOPE_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*\.[a-z0-9]+(-[a-z0-9]+)*$/;

const buildSchema = isEdit => ({
  scope: {
    label: 'Scope',
    rules: [
      onlyWhen(() => !isEdit, required()),
      onlyWhen(
        () => !isEdit,
        pattern(
          SCOPE_REGEX,
          'Format must be resource.action (lowercase, hyphen-separated)'
        )
      ),
    ],
  },
});

const PermissionManagement = () => {
  const token = getAuthToken();
  const [data, setData] = useState({ items: [] });
  const [moduleOptions, setModuleOptions] = useState([]);
  const [modal, setModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Table States
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [sortField, setSortField] = useState('scope');
  const [sortDirection, setSortDirection] = useState('asc');

  const initialFormState = {
    id: null,
    scope: '',
    module: '',
    description: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchModules = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${api.API_URL}/permission/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (Array.isArray(result.data)) {
        setModuleOptions(result.data.map(m => ({ label: m, value: m })));
      }
    } catch (error) {
      console.error('Failed fetching permission modules', error);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = {
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      };

      const qs = new URLSearchParams(queryParams);

      if (debouncedSearchText && debouncedSearchText.trim() !== '') {
        qs.append('Search', debouncedSearchText.trim());
      }

      if (moduleFilter) {
        qs.append('Module', moduleFilter);
      }

      if (sortField) {
        qs.append('SortBy', sortField);
        qs.append('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
      }

      const response = await fetch(
        `${api.API_URL}/permission?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      setData({ items: result.data?.items || [] });
      setTotalRows(result.data?.totalItems || 0);
    } catch (error) {
      notify.apiError(error, 'load data');
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    token,
    debouncedSearchText,
    moduleFilter,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const handleModuleFilterChange = value => {
    setModuleFilter(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchText('');
    setDebouncedSearchText('');
    setModuleFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => handleSearch({ target: { value: '' } }),
    },
    moduleFilter && {
      key: 'module',
      label: 'Module',
      value: moduleFilter,
      onRemove: () => handleModuleFilterChange(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const toggleModal = () => {
    if (modal) {
      setErrors({});
    }
    setModal(!modal);
  };

  const handleEdit = row => {
    setFormData({
      id: row.id,
      scope: row.scope || '',
      module: row.module || '',
      description: row.description || '',
    });
    setModal(true);
  };

  const handleDelete = async (id, scope) => {
    const confirmed = await confirmDelete('permission', scope);

    if (confirmed) {
      try {
        const response = await fetch(`${api.API_URL}/permission/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
              errorData.errors?.[0] ||
              'Delete operation failed'
          );
        }

        notify.success('Permission deleted successfully.');
        fetchData();
      } catch (error) {
        notify.apiError(error, 'delete data');
      }
    }
  };

  const validateForm = () => {
    const tempErrors = runValidation(formData, buildSchema(!!formData.id));
    setErrors(tempErrors);
    if (!hasErrors(tempErrors)) return true;
    focusFirstError(tempErrors, ['scope']);
    return false;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setModalLoading(true);

    try {
      const isEdit = !!formData.id;
      const payload = isEdit
        ? { module: formData.module, description: formData.description }
        : {
            scope: formData.scope,
            module: formData.module,
            description: formData.description,
          };

      const url = isEdit
        ? `${api.API_URL}/permission/${formData.id}`
        : `${api.API_URL}/permission`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.errors?.[0] || 'Save operation failed'
        );
      }

      notify.success(
        `Permission ${isEdit ? 'updated' : 'created'} successfully`
      );
      toggleModal();
      fetchData();
      fetchModules();
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      setModalLoading(false);
    }
  };

  const columns = [
    {
      id: 'scope',
      name: 'Scope',
      selector: row => row.scope,
      sortable: true,
      minWidth: '220px',
      cell: row => (
        <code className='to-title' title={String(row.scope ?? '')}>
          {row.scope}
        </code>
      ),
    },
    {
      id: 'module',
      name: 'Module',
      selector: row => row.module,
      sortable: true,
      minWidth: '160px',
      cell: row => <span>{row.module || '-'}</span>,
    },
    {
      name: 'Description',
      selector: row => row.description,
      minWidth: '260px',
      cell: row => (
        <span className='text-muted' title={String(row.description ?? '')}>
          {row.description || '-'}
        </span>
      ),
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      width: '100px',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleEdit(row)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDelete(row.id, row.scope)}
          />
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
        <div className='email-content minimal-border'>
          <div className='p-4'>
            <div className='to-hero mb-4'>
              <PageBreadcrumb fallback={{ label: 'Permission Management' }} />

              <div className='d-flex align-items-center gap-3 flex-wrap'>
                <span className='to-hero-icon'>
                  <i className='ri-key-2-line' aria-hidden='true' />
                </span>
                <div className='flex-grow-1'>
                  <h4 className='to-hero-title'>Permission Management</h4>
                  <p className='to-hero-subtitle'>
                    Maintain the permission scopes that can be granted to roles.
                  </p>
                </div>
                <div className='d-flex gap-2 flex-wrap'>
                  <button
                    type='button'
                    className='to-hero-btn'
                    onClick={() => fetchData()}
                  >
                    <i className='ri-refresh-line' aria-hidden='true' />
                    Refresh
                  </button>
                  <button
                    type='button'
                    className='to-hero-btn to-hero-btn--solid'
                    onClick={toggleModal}
                  >
                    <i className='ri-add-line' aria-hidden='true' />
                    Add Permission
                  </button>
                </div>
              </div>
            </div>

            <div className='to-panel'>
              <div className='to-toolbar'>
                <ListSearch
                  value={searchText}
                  onChange={handleSearch}
                  placeholder='Search scope, module, or description...'
                  ariaLabel='Search permissions'
                />
                <ListFilterSelect
                  label='Module'
                  value={moduleFilter}
                  onChange={handleModuleFilterChange}
                  allLabel='All Modules'
                  options={moduleOptions}
                />
                <ClearFiltersButton
                  count={filterChips.length}
                  onClick={clearFilters}
                />
                <span className='to-toolbar-meta'>
                  {isLoading
                    ? 'Loading...'
                    : `Showing ${startEntry}–${endEntry} of ${totalRows} permissions`}
                </span>
              </div>

              <ActiveFilterChips chips={filterChips} />

              <div className='to-table-wrap'>
                <DataTable
                  columns={columns}
                  data={data.items}
                  onSort={(column, dir) => {
                    setSortField(column.id);
                    setSortDirection(dir);
                    setCurrentPage(1);
                  }}
                  sortServer
                  highlightOnHover
                  persistTableHead
                  defaultSortFieldId='scope'
                  defaultSortAsc
                  progressPending={isLoading}
                  progressComponent={<SkeletonRows />}
                  noDataComponent={
                    <EmptyState
                      title='No permission found'
                      emptyHint='No permission scopes are defined yet. Permissions are what roles are granted, so add one before assigning access.'
                      filteredHint={`No permission matches ${filterChips
                        .map(chip => `${chip.label} "${chip.value}"`)
                        .join(
                          ', '
                        )}. Try a different keyword or clear the filters.`}
                      hasFilters={hasFilters}
                      onClear={clearFilters}
                      action={{ label: 'Add Permission', onClick: toggleModal }}
                    />
                  }
                  customStyles={{
                    headCells: {
                      style: { paddingLeft: '12px', paddingRight: '12px' },
                    },
                    cells: {
                      style: {
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        wordBreak: 'break-word',
                      },
                    },
                    table: { style: { width: '100%' } },
                  }}
                />
              </div>

              <ListPager
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalRows={totalRows}
                isLoading={isLoading}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handlePerRowsChange}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal}
        toggle={toggleModal}
        size='lg'
        backdrop='static'
        centered
        onClosed={() => setFormData(initialFormState)}
      >
        <ModalHeader toggle={toggleModal} className='bg-light'>
          {formData.id ? 'Edit Permission' : 'Create New Permission'}
        </ModalHeader>
        <ModalBody>
          <RequiredNote />
          <FormGrid columns={1}>
            <TextField
              label='Scope'
              name='scope'
              required={!formData.id}
              placeholder='e.g. procurement.read-all'
              mode={formData.id ? 'readonly' : 'editable'}
              hint={
                formData.id
                  ? 'Cannot be changed after creation'
                  : 'Lowercase resource.action, words separated by hyphens'
              }
              value={formData.scope}
              error={errors.scope}
              onChange={e => {
                setFormData(prev => ({
                  ...prev,
                  scope: e.target.value.trim(),
                }));
                if (errors.scope)
                  setErrors(prev => ({ ...prev, scope: undefined }));
              }}
            />
            <TextField
              label='Module'
              name='module'
              placeholder='e.g. Planning & Procurement'
              value={formData.module}
              onChange={e =>
                setFormData(prev => ({ ...prev, module: e.target.value }))
              }
            />
            <TextAreaField
              label='Description'
              name='description'
              placeholder='e.g. Allows viewing all procurement records'
              value={formData.description}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </FormGrid>
        </ModalBody>
        <ModalFooter>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            onClick={toggleModal}
            disabled={modalLoading}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            icon='ri-save-line'
            onClick={handleSave}
            loading={modalLoading}
          >
            {formData.id ? 'Save Changes' : 'Create Permission'}
          </ActionButton>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default PermissionManagement;
